import React from 'react';

import { BookmarkAdd, BookmarkRemove, FilterAltOff } from '@mui/icons-material';
import { IconButton, Stack, Tooltip } from '@mui/material';

import { Analytics } from '@/app/analytics/Analytics';
import { AnalyticsEvent } from '@/app/analytics/AnalyticsEvent';
import { HighlightableString } from '@/app/log-viewer/highlighting/HighlightableString';
import { HighlightableStringView } from '@/app/log-viewer/highlighting/HighlightableStringView';

type Props = {
  modelData: HeaderModelData;
  isSelected: boolean;
  isBookmarked: boolean;
  passesFilter: boolean;
  onSelectRowAsBaseForTimeOffset: () => void;
  onAddBookmark: () => void;
  onDeleteBookmark: () => void;
};

export interface HeaderModelData {
  lineNumber: HighlightableString;

  dateTime: HighlightableString | null;
  isBaseTimeOffsetFromThisRow: boolean;
  baseTimeOffset: HighlightableString;

  module: HighlightableString | null;
  thread: HighlightableString | null;
  sourceFile: HighlightableString | null;
}

export class Header extends React.Component<Props> {
  render() {
    const data = this.props.modelData;

    return (
      <Stack direction="row" spacing="12px" className="header" alignItems="center">
        <div key="index">
          <Tooltip disableInteractive title="Use Cmd+G to jump to line">
            <a className="rowIndex">
              <HighlightableStringView string={data.lineNumber} />
            </a>
          </Tooltip>
        </div>

        {data.dateTime ? (
          <div
            key="date"
            className="date stop-click-propagation"
            onClick={() => {
              this.props.onSelectRowAsBaseForTimeOffset();
            }}
          >
            <HighlightableStringView string={data.dateTime} />
            &nbsp;
            <span>
              {data.isBaseTimeOffsetFromThisRow ? (
                <b>
                  <HighlightableStringView string={data.baseTimeOffset} />
                </b>
              ) : (
                <HighlightableStringView string={data.baseTimeOffset} />
              )}
            </span>
          </div>
        ) : null}

        {data.module ? (
          <div title="Module" key="module">
            <HighlightableStringView string={data.module} />
          </div>
        ) : null}

        {data.thread ? (
          <div title="Thread">
            <HighlightableStringView string={data.thread} />
          </div>
        ) : null}

        {data.sourceFile ? (
          <div className="sourceFile" title="Source code">
            <HighlightableStringView string={data.sourceFile} />
          </div>
        ) : null}

        {this.props.isBookmarked ? (
          <Tooltip disableInteractive title={<span>Remove bookmark</span>}>
            <IconButton
              className="bookmark-remove-button"
              onClick={() => {
                Analytics.log(AnalyticsEvent.BookmarkRemoved);
                this.props.onDeleteBookmark();
              }}
            >
              <BookmarkRemove fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip disableInteractive title={<span>Add bookmark</span>}>
            <IconButton
              className="bookmark-add-button"
              onClick={() => {
                Analytics.log(AnalyticsEvent.BookmarkAdded);
                this.props.onAddBookmark();
              }}
            >
              <BookmarkAdd fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {!this.props.passesFilter ? (
          <Tooltip
            disableInteractive
            title={
              <span>
                Line does not pass filters.
                <br />
                It is still visible because{' '}
                <b>
                  it{' '}
                  {this.props.isSelected
                    ? 'is selected'
                    : this.props.isBookmarked
                    ? 'is bookmarked'
                    : 'was selected or bookmarked'}
                </b>
                .<br />
                Changing filters may hide it.
              </span>
            }
          >
            <FilterAltOff color="error" fontSize="small" />
          </Tooltip>
        ) : null}
      </Stack>
    );
  }
}
